import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  HashRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom';
import { isElectronShell } from '@/lib/is-electron';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { useAuth } from '@/features/auth/useAuth';

const AuthLayout = lazy(() =>
  import('@/layouts/AuthLayout').then((m) => ({ default: m.AuthLayout })),
);
const AppLayout = lazy(() => import('@/layouts/AppLayout').then((m) => ({ default: m.AppLayout })));
const AdminLayout = lazy(() =>
  import('@/features/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const RealtimeProvider = lazy(() =>
  import('@/components/RealtimeProvider').then((m) => ({ default: m.RealtimeProvider })),
);

const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'));
const InviteLandingPage = lazy(() => import('@/features/invites/pages/InviteLandingPage'));
const AuthCallbackPage = lazy(() => import('@/features/auth/pages/AuthCallbackPage'));
const AuthBridgePage = lazy(() => import('@/features/auth/pages/AuthBridgePage'));
const AuthErrorPage = lazy(() => import('@/features/auth/pages/AuthErrorPage'));
const ChatPage = lazy(() => import('@/features/chat/pages/ChatPage'));
const SettingsPage = lazy(() => import('@/features/profile/pages/SettingsPage'));
const AdminOverviewPage = lazy(() => import('@/features/admin/pages/OverviewPage'));
const AdminLogsPage = lazy(() => import('@/features/admin/pages/AuditLogsPage'));
const AdminMembersPage = lazy(() => import('@/features/admin/pages/MembersAdminPage'));
const AdminRolesPage = lazy(() => import('@/features/admin/pages/RolesPage'));
const AdminChannelsPage = lazy(() => import('@/features/admin/pages/ChannelsPage'));
const AdminCategoriesPage = lazy(() => import('@/features/admin/pages/CategoriesPage'));
const AdminBansPage = lazy(() => import('@/features/admin/pages/BansPage'));
const AdminTimeoutsPage = lazy(() => import('@/features/admin/pages/TimeoutsPage'));
const AdminGeneralPage = lazy(() => import('@/features/admin/pages/GeneralSettingsPage'));
const AdminWelcomeMessagesPage = lazy(() => import('@/features/admin/pages/WelcomeMessagesPage'));
const AdminEmojisPage = lazy(() => import('@/features/admin/pages/EmojisPage'));
const StreamViewerPage = lazy(() => import('@/features/streaming/pages/StreamViewerPage'));
const VoiceChannelPage = lazy(() => import('@/features/voice/pages/VoiceChannelPage'));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function LegacyChannelRedirect() {
  const { channelId } = useParams<{ channelId: string }>();
  return <Navigate to={`/channels/${channelId}`} replace />;
}

function FullPageSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-surface-900">
      <span className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
    </div>
  );
}

export function AppRouter() {
  const Router = isElectronShell() ? HashRouter : BrowserRouter;
  return (
    <Router>
      <AuthProvider>
        <Suspense fallback={<FullPageSpinner />}>
          <Routes>
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/invite/:code" element={<InviteLandingPage />} />
            </Route>

            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/bridge" element={<AuthBridgePage />} />
            <Route path="/auth/error" element={<AuthErrorPage />} />

            <Route
              path="/app"
              element={
                <RequireAuth>
                  <Navigate to="/channels" replace />
                </RequireAuth>
              }
            />

            <Route
              element={
                <RequireAuth>
                  <RealtimeProvider />
                </RequireAuth>
              }
            >
              <Route element={<AppLayout />}>
                <Route index element={<Navigate to="/channels" replace />} />
                <Route path="/channels" element={<ChatPage />} />
                <Route path="/channels/voice/:channelId" element={<VoiceChannelPage />} />
                <Route path="/channels/:channelId" element={<ChatPage />} />
                <Route
                  path="/channels/:serverId/:channelId/stream/:streamId"
                  element={<StreamViewerPage />}
                />

                <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
                <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
                <Route path="/settings/:section" element={<SettingsPage />} />

                <Route path="/channels/@me" element={<Navigate to="/channels" replace />} />
                <Route path="/channels/:serverId/:channelId" element={<LegacyChannelRedirect />} />
              </Route>

              <Route path="/settings/server/:serverId" element={<AdminLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<AdminOverviewPage />} />
                <Route path="logs" element={<AdminLogsPage />} />
                <Route path="members" element={<AdminMembersPage />} />
                <Route path="roles" element={<AdminRolesPage />} />
                <Route path="channels" element={<AdminChannelsPage />} />
                <Route path="categories" element={<AdminCategoriesPage />} />
                <Route path="bans" element={<AdminBansPage />} />
                <Route path="timeouts" element={<AdminTimeoutsPage />} />
                <Route path="general" element={<AdminGeneralPage />} />
                <Route path="welcome-messages" element={<AdminWelcomeMessagesPage />} />
                <Route path="emojis" element={<AdminEmojisPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </Router>
  );
}
